import { Head } from "@inertiajs/react";
import { salonpageR2MenuImageUrl, encodeSalonpagePathId } from "@/lib/_url";
import { SalonpageFooter } from "./_footer";
import { NoroshiReserveCta } from "./_noroshi-reserve-cta";

interface Salon {
  id: string;
  name: string;
  nameKana: string;
}

interface Staff {
  id: string;
  name: string;
  kana: string;
  role: string;
  history: string;
  reserveUrl: string;
}

interface MenuItem {
  id: string;
  category: string;
  name: string;
  priceLabel: string;
  description: string;
  reserveUrl: string;
  imageId?: string | null;
  imageAlt?: string | null;
  position: number;
}

interface Meta {
  title: string;
  description: string;
}

interface FeatureToggles {
  noroshiReserve?: boolean | number | null;
}

interface Props {
  salon?: Salon;
  staff?: Staff | null;
  menu?: MenuItem | null;
  relatedMenus?: MenuItem[];
  featureToggles?: FeatureToggles;
  meta?: Meta;
}

export default function SalonMenu(props: Props) {
  if (!props.salon || !props.menu) return <NotFound label="Menu not found." />;

  const salon = props.salon;
  const staff = props.staff ?? null;
  const menu = props.menu;
  const relatedMenus = props.relatedMenus ?? [];
  const meta = props.meta ?? { title: menu.name, description: `${salon.name}のメニュー詳細です。` };

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur">
            <a href={menusIndexUrl(salon, staff)} className="text-xs font-bold text-zinc-400">← メニュー一覧へ</a>
          </header>

          {menu.imageId && (
            <div className="aspect-[4/3] overflow-hidden bg-gray-100">
              <img src={salonpageR2MenuImageUrl(salon.id, menu.imageId, "gallery")} alt={menu.imageAlt || menu.name} className="h-full w-full object-cover" />
            </div>
          )}

          <section className="space-y-5 p-4">
            <div>
              <p className="text-xs font-bold text-zinc-400">{menu.category || "メニュー"}</p>
              <h1 className="mt-2 text-2xl font-bold leading-8 tracking-tight">{menu.name}</h1>
              <p className="mt-3 text-xl font-bold text-zinc-800">{menu.priceLabel}</p>
            </div>

            {menu.description && <p className="whitespace-pre-line text-[15px] leading-7 text-zinc-700">{menu.description}</p>}

            <NoroshiReserveCta
              salonId={salon.id}
              enabled={props.featureToggles?.noroshiReserve}
              menuId={menu.id}
              staffId={staff?.id}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#070a18] px-4 text-sm font-bold text-white"
            >
              このメニューで予約する
            </NoroshiReserveCta>
          </section>

          {relatedMenus.length > 0 && (
            <section className="border-t border-gray-100 p-4">
              <h2 className="mb-3 text-xl font-bold tracking-tight">ほかのメニュー</h2>
              <div className="divide-y divide-gray-100 border-y border-gray-100">
                {relatedMenus.map((item) => (
                  <a key={item.id} href={menuDetailUrl(salon, item, staff)} className="flex items-start gap-3 py-3">
                    {item.imageId && (
                      <img src={salonpageR2MenuImageUrl(salon.id, item.imageId, "thumbnail")} alt={item.imageAlt || item.name} className="h-20 w-20 shrink-0 rounded-md bg-gray-100 object-cover" />
                    )}
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-zinc-400">{item.category || "メニュー"}</p>
                      <p className="mt-1 line-clamp-2 text-sm font-bold leading-5">{item.name}</p>
                      <p className="mt-1 text-sm font-bold text-zinc-700">{item.priceLabel}</p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

        </main>
        <SalonpageFooter salon={salon} current="menus" />
      </div>
    </>
  );
}

function NotFound({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
      <p className="text-sm text-zinc-400">{label}</p>
    </main>
  );
}

function menuDetailUrl(salon: Salon, menu: MenuItem, staff: Staff | null) {
  const params = new URLSearchParams();
  if (staff) params.set("staff_id", staff.id);
  const query = params.toString();
  return `/salons/${encodeSalonpagePathId(salon.id)}/menus/${encodeSalonpagePathId(menu.id)}${query ? `?${query}` : ""}`;
}

function menusIndexUrl(salon: Salon, staff: Staff | null) {
  const params = new URLSearchParams();
  if (staff) params.set("staff_id", staff.id);
  const query = params.toString();
  return `/salons/${encodeSalonpagePathId(salon.id)}/menus${query ? `?${query}` : ""}`;
}
